import { API } from "@/utils/http"
import axios from "axios";
import { toast } from "@/components/ui/toast/use-toast";

const HOST = '/api/3'

// Project Members
export const fetchMembers = async ({commit, rootState, state}, projectId) => {
  // Try to get orgId from multiple sources
  const orgId = rootState.org.selectedOrg ||
                 rootState.org.current?.organization_id ||
                 state.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const members = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/${projectId}/members`
    }).then(r => r.data)

    commit('SET_MEMBERS', members)
  } catch (e) {
    toast({ title: "Can't fetch members", variant: 'destructive' })
  }
}

export const setMemberRole = async ({commit, dispatch, state}, {member, roleId}) => {
  try {
    await dispatch('access/assignProjectRole', {projectId: member.project_id, memberId: member.member_id, roleId}, {root: true})
    const current = state.members.find(m => m.member_id === member.member_id)
    commit('UPDATE_MEMBER', {...(current ?? member), role_id: roleId})
    toast({ title: 'Role changed' })
    return true
  } catch (e) {
    toast({ title: "Couldn't change the role. Reload and try again.", variant: 'destructive' })
    return false
  }
}

export const fetchAllMembers = async ({commit, rootState, state}) => {
  const orgId = rootState.org.selectedOrg ||
                rootState.org.current?.organization_id ||
                state.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const members = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/members`
    }).then(r => r.data)

    commit('SET_ALL_MEMBERS', members)
  } catch (e) {
    throw new Error(e)
  }
}

export const deleteMember = async ({commit, rootState, state}, payload) => {
  const orgId = rootState.org.selectedOrg ||
                rootState.org.current?.organization_id ||
                state.current?.organization_id

  if (!orgId) {
    return false
  }

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/member/${payload.memberId}`,
    })

    commit('REMOVE_MEMBER', payload.memberId)
    toast({ title: 'Member deleted' })
    return true
  } catch (e) {
    const detail = e.response?.data?.detail
    toast({ title: detail ? `Can't delete member. ${detail}` : "Can't delete member", variant: 'destructive' })
    return false
  }
}

// Invites
export const fetchInvites = async ({commit, rootState}, projectId) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const invites = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/${projectId}/invites`
    }).then(r => r.data)

    commit('SET_INVITES', invites)
  } catch (e) {
    toast({ title: "Can't fetch invites", variant: 'destructive' })
  }
}

export const addInvite = async ({commit, rootState, state}, payload) => {
  const orgId = payload.organization_id || rootState.org.selectedOrg || state.current?.organization_id
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/invite`,
      data: {
        inviter_id: payload.inviter_id,
        project_id: payload.project_id,
        email: payload.email,
        phone: payload.phone,
        fullname: payload.fullname || payload.name || '',
        first_name: payload.first_name,
        last_name: payload.last_name,
        role_id: payload.role_id,
        join_project_ids: payload.join_project_ids || [],
        app_only: payload.app_only
      }
    })
    commit('ADD_INVITE', response.data)
    return response.data ? response.data : null
  } catch (e) {
    const data = e.response?.data
    const reason = data?.message ?? (typeof data?.detail === 'string' ? data.detail : '')
    toast({ title: `Can't send invite. ${reason}`.trim(), variant: 'destructive' })
  }
}

export const deleteInvite = async ({ commit }, uuid) => {
    try {
      await API({
          method: 'DELETE',
          url: `${HOST}/invite/${uuid}`
      })

      commit('REMOVE_INVITE', uuid)
      toast({ title: 'Invite removed' })
      return true
    } catch (e) {
      const detail = e.response?.data?.detail
      toast({ title: detail ? `Can't remove invite. ${detail}` : "Can't remove invite", variant: 'destructive' })
      return false
    }
}

export const resendInvite = async ({commit, dispatch, state}, uuid) => {
  try {
    await API({
      method: 'GET',
      url: `${HOST}/invite/${uuid}/resend`
    })
    toast({ title: 'Invite resent' })

    // Refresh invites list to show updated status
    if (state.current?.project_id) {
      dispatch('fetchInvites', state.current.project_id)
    }
  } catch (e) {
    toast({ title: 'Can\'t resend invite', variant: 'destructive' })
  }
}

export const fetchAllInvites = async({commit}, payload) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/invite`
    })
    const invites = response.data
    commit('SET_ALL_INVITES', invites)
  } catch (e) {
    if (payload?.silent) return
    toast({ title: 'Can\'t fetch invites', variant: 'destructive' })
  }
}

export const fetchAllInvitesFirebase = async({commit}, payload) => {
  try {
    const response = await axios({
      method: 'POST',
      url: `${HOST}/invite/firebase`,
      data: {
        firebase_token: payload.firebaseToken
      }
    })
    commit('SET_ALL_INVITES', response.data)
  } catch (e) {
    toast({ title: 'Can\'t fetch invites', variant: 'destructive' })
  }
}

export const acceptInvite = async ({commit, dispatch}, invite) => {
  const uuid = invite.uuid
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/invite/${uuid}`
    })

    commit('REMOVE_MEMBER_INVITE', uuid)
    toast({ title: 'Invite accepted' })
  } catch (e) {
    toast({ title: 'Can\'t accept invite, try again.', variant: 'destructive' })
  }
}

export const acceptInviteFirebase = async ({commit, dispatch}, payload) => {
  const uuid = payload.invite.uuid
  try {
    const response = await axios({
      method: 'PUT',
      url: `${HOST}/invite/${uuid}/firebase`,
      data: {
        firebase_token: payload.firebaseToken
      }
    })

    commit('REMOVE_MEMBER_INVITE', uuid)
    toast({ title: 'Invite accepted' })
  } catch (e) {
    toast({ title: 'Can\'t accept invite, try again.', variant: 'destructive' })
  }
}

/** Place outlines for the org's projects, keyed by slug path ("ukraine/kyiv"). */
export const fetchPlaceOutlines = async ({ rootState }) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return {}

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/places/outlines`
    })
    return response.data
  } catch {
    return {}
  }
}

export const fetchJoinableProjects = async ({ commit, rootState }) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id
  if (!orgId) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/joinable`
    })
    commit('SET_JOINABLE_PROJECTS', response.data)
  } catch (e) {
    commit('SET_JOINABLE_PROJECTS', [])
  }
}

export const joinProject = async ({ dispatch, rootState }, projectId) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  await API({
    method: 'POST',
    url: `${HOST}/org/${orgId}/project/${projectId}/join`
  })

  await Promise.all([
    dispatch('fetchProjects'),
    dispatch('fetchJoinableProjects'),
    dispatch('access/refresh', orgId, { root: true }).catch(() => {}),
  ])
}

// Project
export const fetchProjects = async ({ commit, dispatch, rootState }) => {
  commit('SET_LOADING', true)

  // Get organization ID from selectedOrg or current org
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    commit('SET_LOADING', false)
    return
  }

  try {
    // Use v2 endpoint with organization_id in path
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project`,
      params: { nested: true }
    })

    const projects = response.data
    commit('SET_PROJECTS', projects)

    try {
      // Also fetch flattened projects from v2 endpoint
      const response = await API({
        method: 'GET',
        url: `${HOST}/org/${orgId}/project`
      })

      commit('SET_FLATED_PROJECTS', response.data)
    } catch (e) {
      toast({ title: 'Can\'t fetch flated projects', variant: 'destructive' })
    }
  } catch (e) {
    console.error(e)
    toast({ title: 'Can\'t fetch projects', variant: 'destructive' })
  } finally {
    commit('SET_LOADING', false)
  }
}

export const fetchProject = async ({rootState, commit, dispatch}, projectID) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/${projectID}`,
    })

    commit('SET_CURRENT', response.data.project)
    commit('SET_CURRENT_MEMBER', response.data.current_member)
    commit('SET_MEMBERS_COUNT', response.data.members_count)
    commit('SET_COMPONENTS', response.data.components)
    commit('SET_PLANET_FEATURES', JSON.parse(response.data.project.planet_features))
  } catch (e) {
    if (!e.response) {
      toast({ title: 'Can\'t fetch project', variant: 'destructive' })
      return
    }
    if (e.response.status === 403) {
      toast({ title: 'You are not member of this project', variant: 'destructive' })
    } else if (e.response.status === 404) {
      toast({ title: 'Project not found', variant: 'destructive' })
    } else {
      toast({ title: 'Can\'t fetch project', variant: 'destructive' })
    }
  }
}

export const updateProject = async ({commit, dispatch, rootState}, project) => {
  const reqData = {...project}
  const orgId = reqData.orgId || rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/project/${project.project_id}`,
      data: {
        project_name: reqData.name,
        project_desc: reqData.description,
        organization_id: reqData.orgId,
        country: reqData.country || null,
        parent_id: parseInt(reqData.parentId) || null,
        ui: reqData.ui || null,
        emoji: reqData.emoji || null,
        workflow_id: reqData.workflow_id ?? null,
      }
    })

    commit('DELETE_PROJECT', response.data)
    commit('ADD_PROJECT', response.data)

    toast({ title: `Project ${response.data.project_name} updated.` })
  } catch (e) {
    toast({ title: 'Can\'t update project. Try again.', variant: 'destructive' })
  }
}

const coverRequest = async (commit, rootState, project, request) => {
  const orgId = project.organization_id || rootState.org.selectedOrg
  const response = await API({
    url: `${HOST}/org/${orgId}/project/${project.project_id}/cover`,
    ...request
  })
  commit('SET_PROJECT_COVER', response.data)
}

export const uploadProjectCover = ({commit, rootState}, {project, file}) => coverRequest(commit, rootState, project, {
  method: 'PUT',
  headers: {'Content-Type': 'multipart/form-data'},
  data: {file}
})

export const setProjectCoverType = ({commit, rootState}, {project, coverType}) => coverRequest(commit, rootState, project, {
  method: 'PATCH',
  data: {cover_type: coverType}
})

export const removeProjectCover = ({commit, rootState}, project) => coverRequest(commit, rootState, project, {
  method: 'DELETE'
})

export const unlistProject = async ({commit}, project) => {
  commit('DELETE_PROJECT', project)
}


export const deleteProject = async ({commit, rootState}, project) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/project/${project.project_id}`,
    }).then(r => r.data)

    commit('DELETE_PROJECT', project)
    toast({ title: `Project ${project.project_name} removed` })
  } catch (e) {
    toast({ title: 'Can\'t remove project. Try again.', variant: 'destructive' })
  }
}

export const createProjectRequest = async ({commit, dispatch, rootState}, projectData) => {
  const reqData = {...projectData}

  // Use orgId from request data or fall back to selectedOrg
  const orgId = reqData.orgId || rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/project/create`,
      data: {
        project_name: reqData.name,
        project_desc: reqData.description,
        organization_id: orgId,
        country: reqData.country || null,
        parent_id: reqData.parentId && reqData.parentId !== '' ? parseInt(reqData.parentId) : null,
        ui: reqData.ui || 'Default',
        emoji: reqData.emoji || null,
        workflow_id: reqData.workflow_id ?? null,
      }
    })

    commit('ADD_PROJECT', response.data)
    toast({ title: `${reqData.name} created` })
    return response.data
  } catch (e) {
    if (!e.response) {
      toast({ title: 'Can\'t create project. Try again.', variant: 'destructive' })
    }
    if (e.response.status === 402) {
      const errorData = e.response.data.detail
      const message = errorData.message
      toast({ title: message, variant: 'destructive' })
    } else if (e.response.status === 403) {
      toast({ title: 'Not enough permissions', variant: 'destructive' })
    } else if (e.response.status === 400) {
      toast({ title: `${e.response.data.detail}`, variant: 'destructive' })
    } else {
      toast({ title: 'Can\'t create project. Try again.', variant: 'destructive' })
    }
  }
}

export const activateProjectForm = async ({commit}, payload) => {
  commit('SET_SHOW_PROJECT_FORM', true)
  commit('SET_PROJECT_FORM_CONTEXT', payload)
}

export const deactivateProjectForm = async ({commit}) => {
  commit('SET_SHOW_PROJECT_FORM', false)
  commit('SET_PROJECT_FORM_CONTEXT', null)
}

// Project Groups
export const createProjectGroup = async ({commit}, data) => {
  const request = {...data}
  try {
    const group = await API({
      method: 'POST',
      url: `${HOST}/project-group/`,
      data: {
        name: request.name,
        country: request.country,
        organization_id: request.org
      }
    }).then(r => r.data)

    commit('ADD_GROUP', group)
    toast({ title: `Project group ${request.name} created` })
  } catch (e) {
    toast({ title: "Can't load project group", variant: 'destructive' })
  }
}

// Project Tags

export const fetchAllTags = async ({commit, rootState}) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    return
  }

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/tag`
    })
    commit('SET_ALL_TAGS', response.data)
  } catch (e) {
    throw new Error(e)
  }
}

export const fetchProjectTags = async ({commit, rootState}, projectID) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/project/${projectID}/tag`
    })
    commit('SET_PROJECT_TAGS', response.data)
  } catch (e) {
    throw new Error(e)
  }
}

export const removeProjectTag = async ({commit, rootState}, payload) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/tag/${payload.tagId}`,
    })
    commit('REMOVE_TAG', payload.tagId)
    toast({ title: 'Tag removed' })
  } catch (e) {
    toast({ title: "Can't remove tag", variant: 'destructive' })
  }
}

export const createProjectTag = async ({commit, rootState}, payload) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/tag`,
      data: {
        project_id: payload.projectId,
        tag_type: payload.tag.tag_type,
        name: payload.tag.name,
        color: payload.tag.color,
        units: payload.tag.unit,
        object_subtype: payload.tag.object_subtype,
        object_type: payload.tag.object_type
      }
    })

    const tag = response.data
    commit('ADD_TAG', tag)
    toast({ title: 'Tag added' })
  } catch (e) {
    toast({ title: "Can't add tag", variant: 'destructive' })
  }
}

export const editProjectTag = async ({commit, rootState}, payload) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/tag/${payload.tag.tag_id}`,
      data: {
        project_id: payload.projectId,
        tag_type: payload.tag.tag_type,
        name: payload.tag.name,
        color: payload.tag.color,
        units: payload.tag.unit,
        object_subtype: payload.tag.object_subtype,
        object_type: payload.tag.object_type
      }
    })

    const tag = response.data
    commit('EDIT_TAG', tag)
    toast({ title: 'Tag added' })
  } catch (e) {
    toast({ title: "Can't add tag", variant: 'destructive' })
  }
}

export const fetchAllComponents = async ({commit, rootState}) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId}/components`
    })

    commit('SET_ALL_COMPONENTS', response.data)
  } catch (e) {
    console.log(e)
  }
}

export const addComponent = async ({commit, rootState}, payload) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/components/${payload.name}`
    })

    const components = response.data
    commit('SET_COMPONENTS', components)

  } catch (e) {
    toast({ title: "Can't add component", variant: 'destructive' })
  }
}

export const removeComponent = async ({commit, rootState}, payload) => {
  const orgId = rootState.org.selectedOrg || rootState.org.current?.organization_id

  if (!orgId) {
    toast({ title: 'No organization selected', variant: 'destructive' })
    return
  }

  try {
    const response = await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId}/project/${payload.projectId}/components/${payload.name}`
    })

    const components = response.data
    commit('SET_COMPONENTS', components)

  } catch (e) {
    toast({ title: "Can't remove component", variant: 'destructive' })
  }
}
